import { useTranslation } from "react-i18next";
import { SlotVideo, usePageAsset } from "~/components/ui";
import { useMediaQuery } from "~/components/use-media-query";
import { cn } from "~/lib/cn";

/** The Home's band exists from the `lg` cut; below it CSS hides the figure, so no video is fetched. */
const SENSORY_BAND_QUERY = "(min-width: 1024px)";

/**
 * T872 S3 — the linen band between the method and Gala (October delivery
 * `figure.sensory-divider`): the `home-sensory` video plays once when it enters the
 * viewport, at most 4 s, never loops, and ends on a still frame; reduced motion or a
 * refused `play()` paints the still from the start (`SlotVideo playOnce`, S2). Absent
 * from the DOM while the slot is empty. Below the `lg` cut CSS hides the figure, and the
 * `<video>` is not mounted at all: a `display: none` video still fetches its poster and
 * metadata, and a phone must not pay for a band it never sees. The figure itself is in
 * the server HTML with the still as its background (`.ho-sensory` in `app.css`), so a
 * desktop paints the still first and the video takes over after hydration on the same
 * frame — no swapped still (T621).
 *
 * T872 S7 — `variant="faq"`: the same linen opens the FAQ's closing (`.faq-sensory`,
 * 160px, 112px below `lg`), at every width, so the video mounts on a phone too; there it
 * is a pause before the help, decorative (the delivery's `alt=""`), not a named figure.
 */
export function SensoryBand({ variant = "home" }: { variant?: "home" | "faq" }) {
  const faq = variant === "faq";
  // T956: the FAQ variant is decorative and reads no copy. Asking for "home" there would
  // download the home dictionary on a page whose route does not declare it.
  const { t } = useTranslation(faq ? [] : "home");
  const asset = usePageAsset("home-sensory");
  const wide = useMediaQuery(SENSORY_BAND_QUERY);
  if (!asset) return null;
  return (
    <figure
      className={cn("ho-sensory", faq && "faq-sensory")}
      aria-label={faq ? undefined : t("sensory.label")}
      aria-hidden={faq ? true : undefined}
    >
      {faq || wide ? <SlotVideo slot="home-sensory" background playOnce className="ho-sensory-video" /> : null}
    </figure>
  );
}
